import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData } from '@zero/shared/util-models';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';
import { CryptoOnboardingInfrastructure } from '../../infrastructure/crypto-onboarding.infrastructure';
import { cryptoOnboardingActions, cryptoOnboardingApiActions } from './crypto-onboarding.actions';
import { cryptoOnboardingFeature } from './crypto-onboarding.reducers';

@Injectable({ providedIn: 'root' })
export class CryptoOnboardingEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #cryptoOnboardingInfrastructure = inject(CryptoOnboardingInfrastructure);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public loadOnboardingStatus$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(cryptoOnboardingActions.loadStatus),
      switchMap(() =>
        this.#cryptoOnboardingInfrastructure.getOnboardingStatus().pipe(
          map((status) => cryptoOnboardingApiActions.loadStatusSuccess({ status })),
          catchError((error) => of(cryptoOnboardingApiActions.loadStatusFailure({ error }))),
        ),
      ),
    );
  });

  public acceptConditions$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(cryptoOnboardingActions.acceptConditions),
      switchMap(() =>
        this.#cryptoOnboardingInfrastructure.postAcceptConditions().pipe(
          map((status) => cryptoOnboardingApiActions.acceptConditionsSuccess({ status })),
          catchError((error) => of(cryptoOnboardingApiActions.acceptConditionsFailure({ error }))),
        ),
      ),
    );
  });

  public handleConditionsAccepted$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(cryptoOnboardingApiActions.acceptConditionsSuccess),
        tap(({ status }) => {
          const { addressValid, identDocValid } = status;
          if (!addressValid) {
            this.#router.navigate(['adressebestaetigen'], { relativeTo: this.getCurrentRoute() });
          } else if (identDocValid && addressValid) {
            this.#router.navigate(['vertragabschliessen'], { relativeTo: this.getCurrentRoute() });
          }
        }),
      );
    },
    { dispatch: false },
  );

  public loadCustomerAddress$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(cryptoOnboardingActions.loadCustomerAddress),
      switchMap(() =>
        this.#cryptoOnboardingInfrastructure.getCustomerAddress().pipe(
          map((address) => cryptoOnboardingApiActions.loadCustomerAddressSuccess({ address })),
          catchError((error) => of(cryptoOnboardingApiActions.loadCustomerAddressFailure({ error }))),
        ),
      ),
    );
  });

  public confirmAddress$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(cryptoOnboardingActions.confirmAddress),
        tap(() => {
          this.#router.navigate(['..', 'vertragabschliessen'], { relativeTo: this.getCurrentRoute() });
        }),
      );
    },
    { dispatch: false },
  );

  public acceptContract$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(cryptoOnboardingActions.acceptContract),
      tap(() => this.#customerStateFacade.loadCustomerBaseData(true)),
      switchMap(() =>
        this.#customerStateFacade.customerBaseData$.pipe(
          filter((customerBaseData) => !!customerBaseData),
          map((customerBaseData) => customerBaseData.customerId),
          take(1),
          concatLatestFrom(() => this.#store.select(cryptoOnboardingFeature.selectAddressConfirmationDate)),
          map(([customerId, addressConfirmationDate]) => {
            const ticket = new Ticket();
            ticket.customerId = customerId;
            ticket.type = 'NEW_KRYPTO_WALLET';
            ticket.ticketData = new TicketData();

            if (addressConfirmationDate) {
              if (!ticket.ticketData.additionalProperties) {
                ticket.ticketData.additionalProperties = {};
              }
              ticket.ticketData.additionalProperties[TicketData.ATTR_KRYPTO_ADDRESS_CONFIRMED_DATETIME] = addressConfirmationDate;
            }

            return new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(ticket));
          }),
          map((dataToAuthorize) => cryptoOnboardingActions.setDataToAuthorize({ dataToAuthorize })),
        ),
      ),
    );
  });

  public handleAuthenticationResultSuccess$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(cryptoOnboardingActions.handleAuthenticationResult),
        tap(() => this.#customerStateFacade.loadCustomerBaseData(true)),
        tap(({ result }) => {
          const { success } = result;
          if (success) {
            this.#router.navigate(['../walleteroeffnet'], { relativeTo: this.getCurrentRoute() });
          }
        }),
      );
    },
    { dispatch: false },
  );

  private getCurrentRoute(): ActivatedRoute {
    let route = this.#router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
}
